/**
 * RESPONSIBILITY: render the flag switchboard at #/secret-flags.
 *
 * Unlisted but open to anyone: flags live in this browser's localStorage, so flipping
 * one affects only the person who flipped it. No server call, no session, no database.
 *
 * Every flag registered with registerFlag() appears here automatically.
 */
import { useState } from 'react';
import { useFlagRows, setFlag, resetFlags, useFlagCode, enableFlagCode } from '../flags/store';
import { describeCode, isValidCode } from '../flags/flag-code';
import { freeCodes, MAX_FLAGS } from '../flags/registry';
import { FLAG_PARAM } from '../flags/url-flags';

export function SecretFlags({ onClose }: { onClose: () => void }) {
  const rows = useFlagRows();
  const code = useFlagCode();

  return (
    <div className="flags-page">
      <div className="container flags-inner">
        <header className="flags-head">
          <div>
            <h1>Secret flags</h1>
            <p>
              Stored in this browser only. Nothing is sent to the server, and nobody
              else is affected by what you switch on here.
            </p>
          </div>
          <button onClick={onClose}>← Back to site</button>
        </header>

        <CodeBox code={code} />

        <ul className="flags-list">
          {rows.map((f) => (
            <li key={f.key} className={f.enabled ? 'on' : ''}>
              <label>
                <input
                  type="checkbox"
                  checked={f.enabled}
                  onChange={(e) => setFlag(f.key, e.target.checked)}
                />
                <span className="flag-body">
                  <span className="flag-label">
                    {f.label} <code>{f.key}</code>
                  </span>
                  <span className="flag-desc">{f.description}</span>
                </span>
                <span className="flag-bit" title={`?${FLAG_PARAM}=${f.code}`}>
                  {f.code}
                </span>
                <span className="flag-state">{f.enabled ? 'ON' : 'off'}</span>
              </label>
            </li>
          ))}
        </ul>

        <div className="flags-foot">
          <button onClick={resetFlags}>Reset to defaults</button>
          <p>
            Register a new one with <code>registerFlag(…)</code> — it appears here with
            no other change. Codes are <strong>one character, case-sensitive</strong>{' '}
            (up to {MAX_FLAGS}); free ones right now:{' '}
            <code>{freeCodes().slice(0, 16).join('')}…</code>
          </p>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The enabled flags as one short string, so a state can be moved between browsers —
 * pasted into a bug report, put in a URL, or read out.
 */
function CodeBox({ code }: { code: string }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const url = `${window.location.origin}/?${FLAG_PARAM}=${code || '…'}`;

  function apply() {
    const t = draft.trim();
    if (!isValidCode(t)) { setError(`Unrecognised: ${describeCode(t)}`); return; }
    enableFlagCode(t);
    setDraft('');
    setError(null);
  }

  return (
    <div className="mask-box">
      <div className="mask-current">
        <span className="mask-label">Enabled</span>
        <code className="mask-value">{code || '—'}</code>
      </div>

      <p className="mask-describe">{code ? describeCode(code) : 'nothing enabled'}</p>

      {code && (
        <p className="mask-describe">
          Share: <code>{url}</code>
        </p>
      )}

      <div className="mask-apply">
        <input
          className="ctl-input"
          placeholder="Paste a code to ENABLE — e.g. AD"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(null); }}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
        />
        <button onClick={apply} disabled={!draft.trim()}>Enable</button>
      </div>

      <p className="ctl-hint">
        Enable-only: a code never switches anything off, so a shared link can only add
        to what someone already has.
      </p>

      {error && <p className="ctl-error">{error}</p>}
    </div>
  );
}
